import setOgmaHeaders from './utils/setOgmaHeaders';
import { getOgmaTranslateUri, getOwsTranslateUri } from './utils/getOgmaUris';
import { isFeatureEnabled } from 'owa-feature-flags';
import getOwsHeaders from './utils/getOwsHeaders';
import { makePostRequest } from 'owa-ows-gateway';

const timeout = 30000;

interface OgmaRequest {
    Data: string;
    DataType: string;
}

interface OgmaResponse {
    TranslatedText: string;
}

export default async function translateText(
    text: string[],
    targetLanguageTag: string,
    correlationId: string
): Promise<string[]> {
    const promise = new Promise<string[]>(function (resolve, reject) {
        if (isFeatureEnabled('rp-inlineTranslation-UseOWS')) {
            translateTextOWS(text, targetLanguageTag, correlationId, resolve, reject);
        } else {
            translateTextOgma(text, targetLanguageTag, correlationId, resolve, reject);
        }
    });

    return promise;
}

async function translateTextOWS(
    texts: string[],
    targetLanguageTag: string,
    correlationId: string,
    resolve,
    reject
) {
    const headers = getOwsHeaders(correlationId);
    if (headers) {
        let timedOut: Boolean = false;
        const timeOutEvent = setTimeout(() => {
            timedOut = true;
            reject('Request timed out');
        }, timeout);

        try {
            const response: OgmaResponse[] = await makePostRequest(
                getOwsTranslateUri(targetLanguageTag),
                texts,
                correlationId,
                false,
                headers,
                true
            );

            if (!timedOut) {
                clearTimeout(timeOutEvent);

                if (response != null) {
                    resolve(getResponse(response));
                } else {
                    reject('OGMA returned invalid response: ' + response);
                }
            }
        } catch (ex) {
            reject('translateTextOWS failed:' + ex.message);
        }
    } else {
        reject('translateTextOWS failed: headers were not set.');
    }
}

function translateTextOgma(
    text: string[],
    targetLanguageTag,
    correlationId: string,
    resolve,
    reject
) {
    const xhttp = new XMLHttpRequest();
    xhttp.timeout = timeout;

    xhttp.onload = function () {
        if (xhttp.status === 200) {
            const translatedText: OgmaResponse[] = JSON.parse(this.responseText);
            if (translatedText != null) {
                resolve(getResponse(translatedText));
            } else {
                reject('OGMA returned invalid response: ' + this.response);
            }
        } else {
            reject('OGMA failed to translate messaage: ' + this.response);
        }
    };

    xhttp.onerror = function () {
        reject('OGMA call failed: ' + this.response);
    };

    xhttp.ontimeout = function () {
        reject('OGMA call timed out');
    };

    xhttp.open('POST', getOgmaTranslateUri(targetLanguageTag));
    const headersAvailable = setOgmaHeaders(xhttp, correlationId);
    if (headersAvailable) {
        xhttp.send(getRequestBody(text));
    } else {
        reject('getSupportedLanguagesOgma failed: headers were not set. ');
    }
}

function getRequestBody(text: string[]): string {
    let ogmaRequest: OgmaRequest[] = [];
    text.forEach(message => {
        const request: OgmaRequest = {
            Data: message,
            DataType: 'html',
        };
        ogmaRequest.push(request);
    });

    return JSON.stringify(ogmaRequest);
}

function getResponse(response: OgmaResponse[]): string[] {
    let result: string[] = [];
    response.forEach(message => {
        result.push(message.TranslatedText);
    });

    return result;
}
